import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	input,
	output,
	signal,
	viewChild,
	ViewEncapsulation,
} from '@angular/core';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { matDateProviders } from '../../../../../shared/helpers/date.func';
import { DatabaseDateFormatService } from '../../services/database-date-format.service';

export type DatabaseReferenceDateVariant = 'global' | 'column';

function startOfToday(): Date {
	const now = new Date();
	return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

@Component({
	selector: 'lvr-database-reference-date-filter',
	imports: [MatDatepickerModule, MatIconButton, MatIcon, TranslocoPipe],
	providers: [matDateProviders],
	templateUrl: './database-reference-date-filter.html',
	styleUrl: './database-reference-date-filter.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		style: 'display: contents;',
	},
	encapsulation: ViewEncapsulation.None,
})
export class DatabaseReferenceDateFilter {
	private readonly dateFormat = inject(DatabaseDateFormatService);

	public readonly value = input<Date | null>(null);
	public readonly disabled = input(false);
	public readonly variant = input<DatabaseReferenceDateVariant>('column');
	public readonly valueChanged = output<Date | null>();
	// Emitted by a column button; the list forwards it to the global control, which owns the picker.
	public readonly openRequested = output<void>();

	private readonly picker = viewChild<MatDatepicker<Date>>('picker');
	protected readonly maxDate = startOfToday();
	protected readonly invalid = signal(false);
	protected readonly hasValue = computed(() => !!this.value());
	protected readonly displayValue = computed(() => {
		const value = this.value();
		return value ? this.dateFormat.formatPickerDate(value) : '';
	});
	protected readonly showClear = computed(() => this.variant() === 'global' && this.hasValue() && !this.disabled());

	public open(): void {
		this.picker()?.open();
	}

	protected onTriggerClick(): void {
		if (this.variant() === 'global') this.open();
		else this.openRequested.emit();
	}

	protected onDateChange(value: Date | null): void {
		if (value && value > this.maxDate) {
			this.invalid.set(true);
			return;
		}
		this.invalid.set(false);
		this.valueChanged.emit(value);
	}

	protected clear(event: Event): void {
		event.stopPropagation();
		this.invalid.set(false);
		this.valueChanged.emit(null);
	}
}
